/**
 * First-party continuous session A/V recording for MCQ / Tech Assessment.
 * Mirrors Video Bot MediaRecorder MIME selection + blob assembly.
 */

const CANDIDATE_MIMETYPES = [
  { mimeType: 'video/webm;codecs=vp9,opus', sendAs: 'video/webm', ext: 'webm' },
  { mimeType: 'video/webm;codecs=vp8,opus', sendAs: 'video/webm', ext: 'webm' },
  { mimeType: 'video/webm', sendAs: 'video/webm', ext: 'webm' },
  { mimeType: 'video/mp4', sendAs: 'video/mp4', ext: 'mp4' },
  { mimeType: 'video/x-matroska;codecs=avc1', sendAs: 'video/x-matroska', ext: 'mkv' },
  { mimeType: 'video/x-matroska', sendAs: 'video/x-matroska', ext: 'mkv' },
];

export function pickSupportedRecorderProfile() {
  if (typeof window === 'undefined' || typeof window.MediaRecorder === 'undefined') return null;
  for (const cand of CANDIDATE_MIMETYPES) {
    try {
      if (window.MediaRecorder.isTypeSupported(cand.mimeType)) return cand;
    } catch { /* ignore */ }
  }
  return { mimeType: '', sendAs: 'video/webm', ext: 'webm' };
}

/** Low-res capture to keep long MCQ/Tech blobs manageable. */
export async function openSessionMedia() {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('Camera/microphone not supported in this browser');
  }
  return navigator.mediaDevices.getUserMedia({
    video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
    audio: { echoCancellation: true, noiseSuppression: true },
  });
}

/**
 * Start MediaRecorder on an existing stream. Uses timeslice for stable chunks;
 * still uploads as a single blob on stop (Video Bot pattern).
 */
export function startSessionRecorder(stream) {
  const profile = pickSupportedRecorderProfile() || { mimeType: '', sendAs: 'video/webm', ext: 'webm' };
  const chunks = [];
  const options = profile.mimeType ? { mimeType: profile.mimeType } : undefined;
  let mr;
  try {
    mr = new MediaRecorder(stream, options);
  } catch {
    mr = new MediaRecorder(stream);
  }
  mr.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  };
  const startedAt = Date.now();
  try {
    mr.start(5000);
  } catch {
    mr.start();
  }

  const stopTracks = () => {
    try {
      stream.getTracks().forEach((t) => t.stop());
    } catch { /* ignore */ }
  };

  const stop = () => new Promise((resolve) => {
    const finish = () => {
      const blob = chunks.length
        ? new Blob(chunks, { type: profile.sendAs })
        : null;
      const durationSec = Math.max(1, Math.round((Date.now() - startedAt) / 1000));
      resolve(blob && blob.size > 0
        ? { blob, durationSec, contentType: profile.sendAs }
        : null);
    };
    if (mr.state === 'inactive') {
      finish();
      return;
    }
    mr.onstop = finish;
    try {
      mr.stop();
    } catch {
      finish();
    }
  });

  return { stop, stopTracks, profile };
}
